import { useEffect, useState, useCallback, useMemo } from "react";
import { format, subDays, addDays } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_KAMAR, STATUS_CHART_COLOR, JAMINAN, RSCM, statusLabel } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LabelList,
} from "recharts";
import { CalendarDays, FilterX, Users, BedDouble, Clock3, Ban, FileCheck2 } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { fillDailyGaps } from "@/lib/dailyGaps";
import WaktuTungguSection from "@/components/WaktuTungguSection";

// Short column headings for the per-Departemen list under the donut (full label in the title tooltip).
const KOLOM_SINGKAT = { "Sudah Dapat Kamar": "Sudah", "Menunggu Kamar": "Waiting", "Batal Operasi": "Batal" };

// Value above a daily bar, drawn only when the bar is wide enough for a two-digit number (narrow phone screens
// squeeze even 31 bars below that); the tooltip still has the value either way. Days filled in with 0 get no number.
const MIN_LABELED_BAR_WIDTH = 14;
const DailyBarLabel = ({ x, y, width, value }) =>
  width < MIN_LABELED_BAR_WIDTH || !value ? null : (
    <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill="#334155">{value}</text>
  );

const fmtTanggal = (t) => {
  try {
    return format(new Date(t + "T00:00:00"), "d MMM", { locale: localeId });
  } catch {
    return t;
  }
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [basis, setBasis] = useState("tanggal_operasi");
  const [filters, setFilters] = useState({
    start: format(subDays(new Date(), 7), "yyyy-MM-dd"),
    end: format(addDays(new Date(), 7), "yyyy-MM-dd"),
    ksm_id: "",
    status_kamar: "",
    jaminan: "",
  });
  const [ksmList, setKsmList] = useState([]);
  const [harian, setHarian] = useState([]);
  const [perKsm, setPerKsm] = useState([]);
  const [perDpjp, setPerDpjp] = useState([]);
  const [statusKamar, setStatusKamar] = useState(null);
  const [tanpaTanggalMasuk, setTanpaTanggalMasuk] = useState(0);   // left out of the "Tgl Masuk Ranap" basis
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/master/ksm").then((r) => setKsmList(r.data)).catch(() => {});
  }, []);

  const params = useMemo(() => ({ basis, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) }), [basis, filters]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [h, k, d, s, t] = await Promise.all([
        api.get("/dashboard/harian", { params }),
        api.get("/dashboard/ksm", { params }),
        api.get("/dashboard/dpjp", { params }),
        api.get("/dashboard/status-kamar", { params }),
        params.basis === "tanggal_masuk_rawat_inap" ? api.get("/dashboard/tanpa-tanggal-masuk", { params }) : { data: { jumlah: 0 } },
      ]);
      setHarian(fillDailyGaps(h.data).map((x) => ({ ...x, label: fmtTanggal(x.tanggal) })));
      setTanpaTanggalMasuk(t.data.jumlah);
      setPerKsm(k.data);
      setPerDpjp(d.data);
      setStatusKamar(s.data);
    } catch (e) {
      // interceptor menangani 401
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const resetFilter = () => {
    setFilters({
      start: format(subDays(new Date(), 7), "yyyy-MM-dd"),
      end: format(addDays(new Date(), 7), "yyyy-MM-dd"),
      ksm_id: "", status_kamar: "", jaminan: "",
    });
  };

  const pieData = statusKamar
    ? STATUS_KAMAR.map((s) => ({ status: s, name: statusLabel(s), value: statusKamar.counts[s] })).filter((x) => x.value > 0)
    : [];
  const totalPasien = statusKamar?.total || 0;
  // Per-bar numbers stay readable up to about a month of bars; past that they collide, so only the tooltip shows them.
  const MAX_LABELED_BARS = 31;
  // Counted on days with data, so the 0 bars filled in between them do not hide the numbers.
  const showHarianLabels = harian.filter((x) => x.jumlah > 0).length <= MAX_LABELED_BARS;
  const valueLabel = { fontSize: 11, fill: "#334155", fontWeight: 600 };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">
          Rekap jadwal pasien rawat inap.
        </p>
      </div>

      {/* Filter bar */}
      <Card className="border-slate-200/80 shadow-sm" data-testid="dashboard-filter-bar">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Dari Tanggal</label>
              <Input type="date" value={filters.start} data-testid="filter-start-date"
                onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Sampai Tanggal</label>
              <Input type="date" value={filters.end} data-testid="filter-end-date"
                onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Departemen</label>
              <Select value={filters.ksm_id || "semua"} onValueChange={(v) => setFilters((f) => ({ ...f, ksm_id: v === "semua" ? "" : v }))}>
                <SelectTrigger data-testid="filter-ksm-select"><SelectValue placeholder="Semua KSM" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="semua">Semua Departemen</SelectItem>
                  {ksmList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Status Kamar</label>
              <Select value={filters.status_kamar || "semua"} onValueChange={(v) => setFilters((f) => ({ ...f, status_kamar: v === "semua" ? "" : v }))}>
                <SelectTrigger data-testid="filter-status-select"><SelectValue placeholder="Semua Status" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="semua">Semua Status</SelectItem>
                  {STATUS_KAMAR.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Jaminan</label>
              <Select value={filters.jaminan || "semua"} onValueChange={(v) => setFilters((f) => ({ ...f, jaminan: v === "semua" ? "" : v }))}>
                <SelectTrigger data-testid="filter-jaminan-select"><SelectValue placeholder="Semua Jaminan" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="semua">Semua Jaminan</SelectItem>
                  {JAMINAN.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={resetFilter} data-testid="filter-reset-button" className="gap-1.5">
              <FilterX size={14} /> Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Statistik ringkas */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-slate-200/80 shadow-sm" data-testid="stat-total">
          <CardContent className="p-4 sm:p-5 flex items-center gap-3.5">
            <div className="h-11 w-11 rounded-xl bg-[#00B9AD]/12 text-[#009E94] flex items-center justify-center shrink-0">
              <Users size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 leading-none">{totalPasien}</p>
              <p className="text-xs text-slate-500 mt-1.5">Total Pasien</p>
            </div>
          </CardContent>
        </Card>
        {STATUS_KAMAR.map((s) => {
          const icons = { "Sudah Dapat Kamar": BedDouble, "Menunggu Kamar": Clock3, "Batal Operasi": Ban };
          const Icon = icons[s];
          return (
            <Card key={s} className="border-slate-200/80 shadow-sm" data-testid={`stat-${s === "Sudah Dapat Kamar" ? "sudah" : s === "Menunggu Kamar" ? "menunggu" : "batal"}`}>
              <CardContent className="p-4 sm:p-5 flex items-center gap-3.5">
                <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${STATUS_CHART_COLOR[s]}1f`, color: s === "Menunggu Kamar" ? "#7c8312" : STATUS_CHART_COLOR[s] }}>
                  <Icon size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 leading-none">{statusKamar?.counts[s] ?? 0}</p>
                  <p className="text-xs text-slate-500 mt-1.5">{statusLabel(s)} ({statusKamar?.persen[s] ?? 0}%)</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
        <Card className="border-slate-200/80 shadow-sm" data-testid="stat-e-kmkb">
          <CardContent className="p-4 sm:p-5 flex items-center gap-3.5">
            <div className="h-11 w-11 rounded-xl bg-[#267B84]/10 text-[#267B84] flex items-center justify-center shrink-0">
              <FileCheck2 size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 leading-none" data-testid="stat-e-kmkb-sudah">
                {statusKamar?.e_kmkb?.Sudah ?? 0}
                <span className="text-sm font-medium text-slate-400"> / {totalPasien}</span>
              </p>
              <p className="text-xs text-slate-500 mt-1.5" data-testid="stat-e-kmkb-belum">
                E-KMKB Sudah (Belum: {statusKamar?.e_kmkb?.Belum ?? 0})
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Widget 1 & 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 border-slate-200/80 shadow-sm" data-testid="widget-pasien-harian">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <CalendarDays size={16} className="text-[#00B9AD]" /> Jumlah Pasien per Hari
              </CardTitle>
              <div className="flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" data-testid="basis-toggle">
                <button
                  onClick={() => setBasis("tanggal_operasi")}
                  data-testid="basis-tanggal-operasi"
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${basis === "tanggal_operasi" ? "bg-[#00B9AD] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                >
                  Tgl Operasi
                </button>
                <button
                  onClick={() => setBasis("tanggal_input")}
                  data-testid="basis-tanggal-input"
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${basis === "tanggal_input" ? "bg-[#00B9AD] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                >
                  Tgl Input
                </button>
                <button
                  onClick={() => setBasis("tanggal_masuk_rawat_inap")}
                  data-testid="basis-tanggal-masuk-rawat-inap"
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${basis === "tanggal_masuk_rawat_inap" ? "bg-[#00B9AD] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                >
                  Tgl Masuk Ranap
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {harian.length === 0 && !loading ? (
              <p className="text-sm text-slate-400 py-16 text-center">Belum ada data pada rentang tanggal ini.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={harian} margin={{ top: 20, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => [v, "Pasien"]} labelFormatter={(l, p) => (p?.[0]?.payload?.tanggal ? fmtDate(p[0].payload.tanggal) : l)} />
                  <Bar dataKey="jumlah" fill={RSCM.primary} radius={[5, 5, 0, 0]} maxBarSize={38}>
                    {showHarianLabels && <LabelList dataKey="jumlah" content={DailyBarLabel} />}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
            {basis === "tanggal_masuk_rawat_inap" && tanpaTanggalMasuk > 0 && (
              <p className="mt-2 text-xs text-slate-500" data-testid="tanpa-tanggal-masuk">
                {tanpaTanggalMasuk} pasien belum ada tanggal masuk ranap tidak ditampilkan
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-5 border-slate-200/80 shadow-sm" data-testid="widget-status-kamar">
          <CardHeader className="pb-0">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <BedDouble size={16} className="text-[#267B84]" /> Status Kamar
            </CardTitle>
          </CardHeader>
          <CardContent>
            {totalPasien === 0 ? (
              <p className="text-sm text-slate-400 py-16 text-center">Belum ada data.</p>
            ) : (
              <>
                <div className="relative">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={3} strokeWidth={2}>
                      {pieData.map((entry) => <Cell key={entry.status} fill={STATUS_CHART_COLOR[entry.status]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center" data-testid="status-kamar-total">
                  <span className="text-xl font-bold text-slate-900 leading-none">{totalPasien}</span>
                  <span className="text-[10px] text-slate-500 mt-1">pasien</span>
                </div>
                </div>
                <ul className="mt-1 space-y-1 text-xs" data-testid="status-kamar-legend">
                  {STATUS_KAMAR.map((s) => (
                    <li key={s} className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: STATUS_CHART_COLOR[s] }} />
                      <span className="text-slate-600">{statusLabel(s)}</span>
                      <span className="ml-auto font-semibold text-slate-900 tabular-nums">{statusKamar?.counts[s] ?? 0}</span>
                      <span className="w-12 text-right text-slate-500 tabular-nums">({statusKamar?.persen[s] ?? 0}%)</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 space-y-1.5" data-testid="status-kamar-per-ksm">
                  <div className="flex items-center justify-between text-[10px] text-slate-500 border-b border-slate-200 pb-1">
                    <span>Per Departemen</span>
                    <span className="flex shrink-0">
                      {STATUS_KAMAR.map((s) => (
                        <span key={s} className="w-14 flex items-center justify-end gap-1" title={statusLabel(s)}>
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_CHART_COLOR[s] }} />
                          {KOLOM_SINGKAT[s]}
                        </span>
                      ))}
                    </span>
                  </div>
                  {statusKamar?.per_ksm?.slice(0, 5).map((k) => (
                    <div key={k.ksm_nama} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1.5">
                      <span className="text-slate-600 font-medium truncate mr-2">{k.ksm_nama}</span>
                      <span className="flex shrink-0">
                        {STATUS_KAMAR.map((s) => (
                          <span key={s} className="w-14 text-right font-semibold text-slate-700 tabular-nums">{k[s]}</span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Widget 3 & 4 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 border-slate-200/80 shadow-sm" data-testid="widget-rekap-ksm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Rekap per Departemen</CardTitle>
          </CardHeader>
          <CardContent>
            {perKsm.length === 0 && !loading ? (
              <p className="text-sm text-slate-400 py-16 text-center">Belum ada data.</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(220, perKsm.length * 42)}>
                <BarChart data={perKsm} layout="vertical" margin={{ top: 0, right: 40, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="ksm_nama" width={130} tick={{ fontSize: 11, fill: "#334155" }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => [v, "Pasien"]} />
                  <Bar dataKey="jumlah" fill={RSCM.secondary} radius={[0, 5, 5, 0]} maxBarSize={24}>
                    <LabelList dataKey="jumlah" position="right" {...valueLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-5 border-slate-200/80 shadow-sm" data-testid="widget-rekap-dpjp">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Rekap per DPJP</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[340px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#267B84] text-white">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold">DPJP</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold hidden sm:table-cell">Departemen</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold">Pasien</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {perDpjp.length === 0 && !loading ? (
                    <tr><td colSpan={3} className="px-4 py-12 text-center text-slate-400 text-sm">Belum ada data.</td></tr>
                  ) : perDpjp.map((d) => (
                    <tr key={`${d.dpjp_nama}|${d.ksm_nama}`} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-medium text-slate-800">
                        {d.dpjp_nama}
                        {/* One doctor typed several ways is one row (grouped by the backend, data unchanged). */}
                        {d.jumlah_ejaan > 1 && (
                          <span className="block text-[11px] font-normal text-slate-400" title="Ejaan berbeda di data pasien dihitung sebagai satu DPJP"
                            data-testid="dpjp-gabungan-ejaan">
                            gabungan {d.jumlah_ejaan} ejaan
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 text-xs hidden sm:table-cell">{d.ksm_nama}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-[#00B9AD]/12 text-[#009E94] text-xs font-bold">
                          {d.jumlah}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Super Admin only; the endpoint itself answers 403 to every other role. */}
      {user?.role === "super_admin" && <WaktuTungguSection params={params} />}
    </div>
  );
}
